import { ArrowRightOutlined } from "@ant-design/icons";
import { Skeleton } from "antd";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

interface StatCardProps {
	label: string;
	value: number | string;
	icon?: ReactNode;
	/** Muted line under the value, e.g. "oxirgi 30 kun". */
	hint?: string;
	loading?: boolean;
	/** Tints the icon chip — use to distinguish a status metric. */
	tone?: "neutral" | "accent" | "success" | "warning";
	/** Route to navigate to when set — renders the card as a link. */
	to?: string;
}

const toneStyles: Record<NonNullable<StatCardProps["tone"]>, string> = {
	neutral: "bg-[var(--c-hover)] text-[var(--c-text-secondary)]",
	accent: "bg-[var(--c-accent-soft)] text-[var(--c-accent)]",
	success: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
	warning: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
};

export function StatCard({
	label,
	value,
	icon,
	hint,
	loading = false,
	tone = "neutral",
	to,
}: StatCardProps) {
	const content = (
		<>
			<div className="flex items-start justify-between gap-3">
				<span className="text-[13px] font-medium text-[var(--c-text-secondary)]">{label}</span>
				{icon ? (
					<span
						className={`flex size-8 shrink-0 items-center justify-center rounded-lg text-[15px] ${toneStyles[tone]}`}
					>
						{icon}
					</span>
				) : null}
			</div>

			{loading ? (
				<Skeleton.Button active size="small" className="mt-3 !h-8 !w-20" />
			) : (
				<div className="numeric mt-2 text-[28px] font-semibold leading-tight tracking-[-0.02em] text-[var(--c-text)]">
					{typeof value === "number" ? value.toLocaleString("uz-UZ") : value}
				</div>
			)}

			{hint ? (
				<div className="mt-1 flex items-center justify-between gap-2">
					<span className="text-xs text-[var(--c-text-muted)]">{hint}</span>
					{to ? (
						<ArrowRightOutlined className="text-[11px] text-[var(--c-text-muted)] opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
					) : null}
				</div>
			) : to ? (
				<div className="mt-1 flex justify-end">
					<ArrowRightOutlined className="text-[11px] text-[var(--c-text-muted)] opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
				</div>
			) : null}
		</>
	);

	if (to) {
		return (
			<Link
				to={to}
				className="group block rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)] p-5 outline-none transition-colors hover:border-[var(--c-accent)] focus-visible:border-[var(--c-accent)]"
			>
				{content}
			</Link>
		);
	}

	return (
		<div className="rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)] p-5 transition-colors hover:border-[var(--c-text-muted)]">
			{content}
		</div>
	);
}
